// src/playBilling.js
// Cobro con Google Play DENTRO de la app de Android (TWA), usando la Digital Goods API.
// En el navegador web normal esto NO está disponible → la app usa Stripe como siempre.
//
// IDs de los productos: deben coincidir EXACTAMENTE con los que crees en Google Play Console.
export const PLAY_PRODUCTS = {
  monthly: "pollo_mensual", // suscripción $9.99/mes
  yearly: "pollo_anual",    // suscripción $99.99/año
};

const PLAY_BILLING_METHOD = "https://play.google.com/billing";

// ¿Estamos dentro de la app de Android con cobro de Google disponible?
export function isPlayBillingAvailable() {
  return (
    typeof window !== "undefined" &&
    "getDigitalGoodsService" in window &&
    typeof window.PaymentRequest === "function"
  );
}

async function getService() {
  if (!isPlayBillingAvailable()) return null;
  try {
    return await window.getDigitalGoodsService(PLAY_BILLING_METHOD);
  } catch (e) {
    return null;
  }
}

// Lanza la compra de Google Play para un plan ("monthly" | "yearly").
// Devuelve { ok, purchaseToken, productId } o { ok:false, reason }.
export async function comprarConGooglePlay(plan) {
  const productId = PLAY_PRODUCTS[plan] || PLAY_PRODUCTS.monthly;
  const service = await getService();
  if (!service) return { ok: false, reason: "no_disponible" };

  // 1. Pedir detalles del producto (precio, moneda) a Google.
  let details;
  try {
    details = await service.getDetails([productId]);
  } catch (e) {
    return { ok: false, reason: "sin_detalles" };
  }
  if (!details || details.length === 0) return { ok: false, reason: "producto_no_encontrado" };
  const item = details[0];

  // 2. Mostrar la hoja de pago nativa de Google Play.
  let request;
  try {
    request = new PaymentRequest(
      [{ supportedMethods: PLAY_BILLING_METHOD, data: { sku: productId } }],
      { total: { label: "Pollo IA", amount: { currency: item.price.currency, value: item.price.value } } }
    );
  } catch (e) {
    return { ok: false, reason: "payment_request_error" };
  }

  try {
    const response = await request.show();
    const purchaseToken = response.details && response.details.purchaseToken;
    // Cerramos la hoja de pago como exitosa (la verificación real la hace el servidor).
    await response.complete("success");
    if (!purchaseToken) return { ok: false, reason: "sin_token" };
    return { ok: true, purchaseToken: purchaseToken, productId: productId };
  } catch (e) {
    // El usuario canceló o falló.
    return { ok: false, reason: "cancelado" };
  }
}

// Devuelve las compras ACTIVAS del usuario en Google Play (con purchaseToken).
// Devuelve null si no se pudo consultar (para NO quitar acceso por un error de red).
export async function comprasExistentes() {
  const service = await getService();
  if (!service) return null;
  try {
    const purchases = await service.listPurchases();
    return purchases || [];
  } catch (e) {
    return null;
  }
}

// Sincroniza el acceso del usuario con lo que Google Play dice AHORA mismo:
// - si tiene una suscripción activa de Pollo IA → mantiene/da acceso.
// - si NO tiene ninguna activa (canceló/venció) → le quita el acceso de Play.
// Si no se pudo consultar (error/red), no cambia nada (no revoca por error).
export async function syncPlayEntitlement(email) {
  if (!isPlayBillingAvailable() || !email) return;
  const purchases = await comprasExistentes();
  if (purchases === null) return; // no se pudo consultar → no tocar nada
  const activa = purchases.find(function (p) {
    return p && (p.itemId === PLAY_PRODUCTS.monthly || p.itemId === PLAY_PRODUCTS.yearly);
  });
  try {
    if (activa) {
      await fetch("/api/verify-play-purchase", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email, purchaseToken: activa.purchaseToken, productId: activa.itemId }),
      });
    } else {
      // Sin suscripción activa en Google Play → quitar el acceso de Play.
      await fetch("/api/verify-play-purchase", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email, noPurchase: true }),
      });
    }
  } catch (e) { /* sin red: no cambiar nada */ }
}
